import * as React from 'react'
import { Questionnaire as QuestionnairePrimitive } from '@shadcn/react/questionnaire'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'

function Questionnaire({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Root>) {
  return (
    <QuestionnairePrimitive.Root
      data-slot="questionnaire"
      className={cn('flex flex-col gap-4', className)}
      {...props}
    />
  )
}

function QuestionnaireProgress({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Progress>) {
  return (
    <QuestionnairePrimitive.Progress
      data-slot="questionnaire-progress"
      className={cn('text-xs font-medium text-muted-foreground', className)}
      {...props}
    />
  )
}

function QuestionnaireItem({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Item>) {
  return (
    <QuestionnairePrimitive.Item
      data-slot="questionnaire-item"
      className={cn('flex min-w-0 flex-col gap-3 border-0 p-0', className)}
      {...props}
    />
  )
}

function QuestionnaireTitle({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Title>) {
  return (
    <QuestionnairePrimitive.Title
      data-slot="questionnaire-title"
      className={cn('text-base font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function QuestionnaireDescription({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Description>) {
  return (
    <QuestionnairePrimitive.Description
      data-slot="questionnaire-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

function QuestionnaireChoices({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Choices>) {
  return (
    <QuestionnairePrimitive.Choices
      data-slot="questionnaire-choices"
      className={cn('grid gap-2', className)}
      {...props}
    />
  )
}

function QuestionnaireChoice({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Choice>) {
  return (
    <QuestionnairePrimitive.Choice
      data-slot="questionnaire-choice"
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg border bg-background p-3 text-sm transition-colors hover:bg-accent has-[:focus-visible]:border-ring has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/40 data-checked:border-primary data-checked:bg-primary/5',
        className,
      )}
      {...props}
    />
  )
}

function QuestionnaireChoiceInput({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.ChoiceInput>) {
  return (
    <QuestionnairePrimitive.ChoiceInput
      data-slot="questionnaire-choice-input"
      className={cn('size-4 accent-primary', className)}
      {...props}
    />
  )
}

function QuestionnaireChoiceLabel({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.ChoiceLabel>) {
  return (
    <QuestionnairePrimitive.ChoiceLabel
      data-slot="questionnaire-choice-label"
      className={cn('min-w-0 flex-1', className)}
      {...props}
    />
  )
}

function QuestionnaireChoiceShortcut({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.ChoiceShortcut>) {
  return (
    <QuestionnairePrimitive.ChoiceShortcut
      data-slot="questionnaire-choice-shortcut"
      className={cn('rounded border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground', className)}
      {...props}
    />
  )
}

function QuestionnaireInput({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Input>) {
  return (
    <QuestionnairePrimitive.Input
      data-slot="questionnaire-input"
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40',
        className,
      )}
      {...props}
    />
  )
}

function QuestionnaireError({ className, ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Error>) {
  return (
    <QuestionnairePrimitive.Error
      data-slot="questionnaire-error"
      className={cn('text-xs text-destructive', className)}
      {...props}
    />
  )
}

function QuestionnaireActions({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="questionnaire-actions" className={cn('flex items-center justify-between gap-2', className)} {...props} />
}

function QuestionnairePrevious({ className, children = 'Previous', ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Previous>) {
  return (
    <QuestionnairePrimitive.Previous
      data-slot="questionnaire-previous"
      render={<Button type="button" variant="ghost" size="sm" className={className} />}
      {...props}
    >
      <ChevronLeft data-icon="inline-start" />
      {children}
    </QuestionnairePrimitive.Previous>
  )
}

function QuestionnaireNext({ className, children = 'Next', ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Next>) {
  return (
    <QuestionnairePrimitive.Next
      data-slot="questionnaire-next"
      render={<Button type="button" size="sm" className={className} />}
      {...props}
    >
      {children}
      <ChevronRight data-icon="inline-end" />
    </QuestionnairePrimitive.Next>
  )
}

function QuestionnaireSkip({ className, children = 'Skip', ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Skip>) {
  return (
    <QuestionnairePrimitive.Skip
      data-slot="questionnaire-skip"
      render={<Button type="button" variant="ghost" size="sm" className={className} />}
      {...props}
    >
      {children}
    </QuestionnairePrimitive.Skip>
  )
}

function QuestionnaireSubmit({ className, children = 'Send request', ...props }: React.ComponentProps<typeof QuestionnairePrimitive.Submit>) {
  return (
    <QuestionnairePrimitive.Submit
      data-slot="questionnaire-submit"
      render={<Button type="submit" size="sm" className={className} />}
      {...props}
    >
      {children}
    </QuestionnairePrimitive.Submit>
  )
}

export {
  Questionnaire,
  QuestionnaireActions,
  QuestionnaireChoice,
  QuestionnaireChoiceInput,
  QuestionnaireChoiceLabel,
  QuestionnaireChoiceShortcut,
  QuestionnaireChoices,
  QuestionnaireDescription,
  QuestionnaireError,
  QuestionnaireInput,
  QuestionnaireItem,
  QuestionnaireNext,
  QuestionnairePrevious,
  QuestionnaireProgress,
  QuestionnaireSkip,
  QuestionnaireSubmit,
  QuestionnaireTitle,
}
